import React, { useEffect, useState } from "react";
import VuiBox from "components/ui/VuiBox";
import PieChart from "components/charts/PieChart";
import { usePieChartData } from "./pieChartData";
import { getPieChartOptions } from "./pieChartOptions";
import { useAnalytics } from "context/AnalyticsContext";
import ChartInfoTooltip from "components/Tooltip/ChartInfoTooltip";
import { chartTooltips } from "components/Tooltip/Definitions/data";

import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import StandardMessageDisplay from "components/StandardMessageDisplay";
import { SM_PLATFORM } from "../../digitalHub";
import GlassCard from "components/GlassCard";

function ContentDistribution({ platform, companyId, chartType, isCompanyView }) {

  const { selectedPresetKey, competitors, companyName } = useAnalytics();

  // Fetch data based on chartType
  const { 
    pieChartData,
    labels,
    otherSliceDetails,
    loading,
    error
  } = usePieChartData(companyId, platform, chartType, selectedPresetKey);

  const [chartOptions, setChartOptions] = useState({});

  // Get the company name
  const getDisplayName = () => {

    if (!companyId || companyId === '') {
      return '';
    }
    const competitor = competitors.find(c => c.id === companyId);
    if (competitor) {
      return competitor.name;
    }
    // Default case
    return companyName || 'Company';
  };

  useEffect(() => {
    // Update options with labels and otherSliceDetails
    const updatedOptions = {
      ...getPieChartOptions(labels, isCompanyView, otherSliceDetails, pieChartData),
      labels: labels
    };
    setChartOptions(updatedOptions);
  }, [labels, isCompanyView, otherSliceDetails, pieChartData]);

  // Generate title based on chartType and platform
  const getChartTitle = () => {
    const displayName = getDisplayName();

    // Determine the main part of the title based on chartType
    let analysisType;
    if (chartType === 'postType') {
      analysisType = 'Content Breakdown';
    } else if (chartType === 'writingTone') { // Assuming 'writingTone' is the key for Communication Goals
      analysisType = 'Communications Goals';
    } else {
      analysisType = 'Analysis'; // Fallback for any other chartType
    }
    
    return `${displayName} ${SM_PLATFORM[platform]} ${analysisType}`;
  };

  // Generate tooltip key based on chartType and platform
  const getChartTooltipKey = () => {
    if (platform === "linkedin") {
      return chartType === 'postType' ? "linkedinContentBreakdown" : "linkedinMessagingStyle";
    } else if (platform === "x") {
      return chartType === 'postType' ? "xContentBreakdown" : "xMessagingStyle";
    }
    // Fallback/Default (may need adjustment if more platforms/types are added)
    return chartType === 'postType' ? "linkedinContentBreakdown" : "linkedinMessagingStyle"; 
  };

  const chartTypeName = chartType === 'postType' ? 'content type' : 'writing tone';

  // Simplified return: renders a single card with the specified chart
  return (
    <GlassCard sx={{ overflow: 'visible', minHeight: '380px' }}>
      <VuiBox>
        <ChartInfoTooltip
          title={getChartTitle()}
          tooltipText={chartTooltips[getChartTooltipKey()]}
        >
          <VuiBox
            sx={{
              height: "300px",
              padding: "10px",
              overflow: "visible",
            }}
          >
            {loading ? (
              <StandardLoadingIndicator containerHeight="100%" />
            ) : error ? (
              <StandardMessageDisplay
                type="error"
                title={`Error Loading ${chartTypeName}`}
                message={`Unable to load ${chartTypeName} distribution. Please try again.`}
                containerHeight="100%"
              />
            ) : !pieChartData || pieChartData.length === 0 ? (
              <StandardMessageDisplay
                type="noData"
                title={`No ${chartTypeName} Data`}
                message={`No ${chartTypeName} data from ${getDisplayName()} was recorded in the chosen timeframe.`}
                containerHeight="100%"
              />
            ) : (
              <PieChart
                pieChartData={pieChartData}
                pieChartOptions={chartOptions}
              />
            )}
          </VuiBox>
        </ChartInfoTooltip>
      </VuiBox>
    </GlassCard>
  );
}

export default ContentDistribution;